import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { Lightning, Check, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [status, setStatus] = useState(token ? 'verifying' : 'idle');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.post('/saas/verify-email', { token });
        setStatus('success');
        setMessage(data.message);
      } catch (err) {
        setStatus('error');
        setMessage(formatApiError(err));
      }
    })();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    try {
      await api.post('/saas/resend-verification', { email: resendEmail });
      setResendSent(true);
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <Lightning size={24} weight="fill" className="text-foreground" />
          <span className="font-heading text-lg font-bold text-foreground">Mayr Lead Creator</span>
        </Link>

        {status === 'verifying' && (
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Verifying your email…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center" data-testid="verify-success">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <Check size={24} className="text-emerald-600" weight="bold" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Email verified</h1>
            <p className="text-sm text-muted-foreground mb-6">{message}</p>
            <Button onClick={() => navigate('/signin')} className="w-full" data-testid="go-signin-btn">Continue to sign in</Button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Warning size={24} className="text-destructive" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2 text-center">Verification failed</h1>
            <p className="text-sm text-muted-foreground mb-6 text-center">{message}</p>

            {!resendSent ? (
              <form onSubmit={handleResend} className="space-y-3">
                <label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Request a new link</label>
                <Input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} placeholder="you@example.com" required data-testid="resend-email-input" />
                <Button type="submit" className="w-full" data-testid="resend-verify-btn">Resend verification email</Button>
              </form>
            ) : (
              <div className="p-4 rounded-md bg-muted/50 text-sm text-center text-muted-foreground">
                If an account exists with that email, a new verification link has been sent.
              </div>
            )}
          </div>
        )}

        {status === 'idle' && (
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2 text-center">Verify your email</h1>
            <p className="text-sm text-muted-foreground mb-6 text-center">Didn't get the email? Enter your address below to resend it.</p>
            {!resendSent ? (
              <form onSubmit={handleResend} className="space-y-3">
                <Input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} placeholder="you@example.com" required data-testid="resend-email-input" />
                <Button type="submit" className="w-full" data-testid="resend-verify-btn">Send verification email</Button>
              </form>
            ) : (
              <div className="p-4 rounded-md bg-muted/50 text-sm text-center text-muted-foreground">
                If an account exists with that email, a new verification link has been sent.
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-6 text-center">
          <Link to="/signin" className="underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
